import React, { useCallback, useEffect } from 'react';
import { ReactComponent as Tools } from '../../assets/icons/tools.svg';
import { ReactComponent as ToolsHover } from '../../assets/icons/tools-hover.svg';
import { ReactComponent as Page } from '../../assets/icons/page.svg';
import { ReactComponent as PageHover } from '../../assets/icons/page-hover.svg';
import Arrow from '../../assets/icons/arrow.svg';
import { ReactComponent as AnnotationIcon } from '../../assets/icons/annotationIcon.svg';
import { useStyles } from './SideToolbar.styles';
import CancelIcon from '@material-ui/icons/Cancel';
import { PreviewType, SolutionViewMode } from '../../pages/Solution/Solution.component';
import TooltipOneComponent from '../TooltipOne/TooltipOne.component';
import { fill, fillHover } from '../../utils/contrastSvgAttributes';
import { useLayoutContext } from '../../Context/Layout.context';
import CommentIcon from '../CommentDraggableIcon/CommentIcon.component';

interface SideToolbarProps {
  mode: SolutionViewMode;
  previewType: PreviewType;
  isToolbarOpen: boolean;
  isThumbnailsOpen: boolean;
  isFullscreen: boolean;
  handleToolbarOpen?: () => void;
  handleThumbnailsOpen?: () => void;
  handleAnnotationsOpen?: () => void;
  onCommentActive: (isActive: boolean) => void;
  isNewCommentActive: boolean;
  canAddComment: boolean;
  isAnnotationToolOpen: boolean;
  disabled: boolean;
}

export const SideToolbar: React.FC<SideToolbarProps> = (props) => {
  const classes = useStyles();

  const { isAnyDropdownOpen, isCommentPopoverOpen } = useLayoutContext();

  const commentActiveShortcutListener = useCallback(
    (event: KeyboardEvent) => {
      if (isCommentPopoverOpen.current || !props.canAddComment) return;
      if (event.key === 'k') props.onCommentActive(true);
      if (event.key === 'Escape') props.onCommentActive(false);
    },
    [isCommentPopoverOpen.current, props.onCommentActive],
  );

  useEffect(() => {
    document.addEventListener('keydown', commentActiveShortcutListener);
    return () => {
      document.removeEventListener('keydown', commentActiveShortcutListener);
    };
  }, [commentActiveShortcutListener]);

  return (
    <div
      id="sideBar"
      className={
        !props.isFullscreen
          ? `${classes.sideToolbar} ${classes.sideToolbarNonFullscreen}`
          : `${classes.sideToolbar} ${classes.sideToolbarFullscreen}`
      }
    >
      <TooltipOneComponent title="Narzędzia" placement="right">
        <div
          onClick={props.handleToolbarOpen}
          className={classes.sideToolbarIcon}
          style={{ cursor: props.disabled ? 'not-allowed' : 'default' }}
          draggable="false"
          tabIndex={0}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
              isAnyDropdownOpen.current = !props.isToolbarOpen;
              props.handleToolbarOpen && props.handleToolbarOpen();
            }
          }}
        >
          {props.isToolbarOpen ? (
            <ToolsHover fill={fillHover} title="ikona narzędzi" role="img" />
          ) : (
            <>
              <div className={classes.relativeWrapper}>
                {props.disabled && <CancelIcon className={classes.disabledAction} />}
                <Tools fill={fill} aria-label="ikona narzędzi" role="img" />
              </div>
            </>
          )}
        </div>
      </TooltipOneComponent>
      {props.isToolbarOpen && (
        <img className={`${classes.arrowToolbarIcon} ${classes.arrowToolbarIconTop}`} src={Arrow} alt="ikona nawigacji" />
      )}
      <TooltipOneComponent title="Miniatury stron" placement="right">
        <div
          onClick={props.previewType === 'card' ? props.handleThumbnailsOpen : undefined}
          className={classes.sideToolbarIcon}
          style={{ cursor: props.previewType === 'card' ? 'pointer' : 'not-allowed' }}
          draggable="false"
          tabIndex={0}
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter') {
              isAnyDropdownOpen.current = props.isThumbnailsOpen && props.previewType === 'card';
              props.handleThumbnailsOpen && props.handleThumbnailsOpen();
            }
          }}
        >
          {props.previewType === 'card' ? (
            <PageHover fill={fill} title="ikona miniatury stron" />
          ) : (
            <>
              <div className={classes.relativeWrapper}>
                <CancelIcon className={classes.disabledAction} />
                <Page fill={fill} aria-label="ikona Miniatury stron" />
              </div>
            </>
          )}
        </div>
      </TooltipOneComponent>
      {props.isThumbnailsOpen && (
        <img className={`${classes.arrowToolbarIcon} ${classes.arrowToolbarIconBottom}`} src={Arrow} alt="ikona strzałka" />
      )}
      {props.canAddComment && props.mode !== SolutionViewMode.sampleAssessmentGroupAssignment && (
        <CommentIcon
          onCommentActiveChange={props.onCommentActive}
          isActive={props.isNewCommentActive}
        />
      )}
      {props.isAnnotationToolOpen && (
        <TooltipOneComponent title="Adnotacje" placement="right">
          <div
            onClick={props.handleAnnotationsOpen}
            className={classes.sideToolbarIcon}
            draggable="false"
            tabIndex={0}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter') {
                isAnyDropdownOpen.current = !isAnyDropdownOpen.current;
                props.handleAnnotationsOpen && props.handleAnnotationsOpen();
              }
            }}
          >
            <AnnotationIcon fill={fill} title="otwórz panel adnotacji" />
          </div>
        </TooltipOneComponent>
      )}
    </div>
  );
};
